import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, FileText, X, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { useSearch, useCategories } from "@/hooks/useKBData";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDistanceToNow } from "date-fns";

interface KBSearchCommandProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KBSearchCommand({ isOpen, onClose }: KBSearchCommandProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  // Debounce search query to prevent API spam
  const debouncedQuery = useDebounce(query, 300);

  // Fetch search results and categories
  const { data: searchData, isLoading: searchLoading } = useSearch(debouncedQuery);
  const { data: categoriesData } = useCategories();

  const searchResults = searchData?.articles || [];
  const categories = categoriesData?.categories || [];

  // Mock recent searches for now
  const recentSearches = ["CRM setup", "brand guidelines", "lead management"];

  const handleArticleSelect = (slug: string) => {
    navigate(`/app/kb/${slug}`);
    onClose();
  };

  const handleCategorySearch = (categoryName: string) => {
    setQuery(categoryName.toLowerCase());
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
        <Command className="rounded-2xl border-0" shouldFilter={false}>
          {/* Search Input */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search articles..."
              className="flex-1 bg-transparent text-lg border-0 outline-none placeholder:text-muted-foreground [&_svg]:hidden"
            />
            {searchLoading && debouncedQuery && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <CommandList className="max-h-[60vh]">
            {/* No Query State - Recent Searches */}
            {debouncedQuery.length === 0 && (
              <>
                <CommandGroup heading="Recent Searches" className="p-4 pb-2">
                  {recentSearches.map((search) => (
                    <CommandItem
                      key={search}
                      onSelect={() => setQuery(search)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer data-[selected=true]:bg-muted"
                    >
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{search}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>

                <CommandGroup heading="Browse by Category" className="px-4 pb-4">
                  <div className="flex flex-wrap gap-2 pt-2">
                    {categories.map((category) => (
                      <Badge
                        key={category.id}
                        variant="outline"
                        className="cursor-pointer hover:bg-muted transition-colors px-3 py-1.5 rounded-lg text-xs"
                        onClick={() => handleCategorySearch(category.name)}
                      >
                        {category.icon} {category.name}
                      </Badge>
                    ))}
                  </div>
                </CommandGroup>
              </>
            )}

            {/* Search Results */}
            {debouncedQuery.length > 0 && searchResults.length > 0 && (
              <CommandGroup heading={`Results (${searchResults.length})`} className="p-2">
                {searchResults.map((article) => {
                  const category = categories.find((c) => c.id === article.category_id);
                  return (
                    <CommandItem
                      key={article.id}
                      onSelect={() => handleArticleSelect(article.slug)}
                      className="flex items-start gap-3 px-3 py-3 rounded-xl cursor-pointer data-[selected=true]:bg-primary/10"
                    >
                      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-sm truncate">{article.title}</span>
                          <ArrowRight className="h-3 w-3 text-primary shrink-0 data-[selected=false]:opacity-0" />
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {article.excerpt || "No preview available"}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {category?.name || "Uncategorized"}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            Updated {formatDistanceToNow(new Date(article.updated_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* No Results */}
            {debouncedQuery.length > 0 && !searchLoading && searchResults.length === 0 && (
              <CommandEmpty className="p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold mb-1">No results found</h3>
                <p className="text-sm text-muted-foreground">
                  Try searching with different keywords
                </p>
              </CommandEmpty>
            )}

            {/* Loading State */}
            {debouncedQuery.length > 0 && searchLoading && (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Searching...</p>
              </div>
            )}
          </CommandList>
        </Command>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">↓</kbd>
              <span>Navigate</span>
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">↵</kbd>
              <span>Open</span>
            </span>
          </div>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">esc</kbd>
            <span>Close</span>
          </span>
        </div>
      </div>
    </div>
  );
}